/**
 * Material aus dem Stamm wählen — oder neu anlegen, wenn es das noch nicht gibt.
 *
 * Gebraucht auf der Lieferantenseite (Material in die Liste) und am Projekt
 * (Material von Hand dazu). Neu angelegt wird über `POST /kontor/material` mit
 * derselben Namensgleich-Prüfung wie in ScanFlow: „humus" liefert das
 * vorhandene „Humus" statt einer Dublette.
 */
import { useEffect, useState } from "react";

import { fehlerText } from "@vimaana/ui";

import { useMaterialAnlegen, useMaterialStammlisten, useMaterialSuche, type MaterialArt, type MaterialStamm } from "../../hooks/useLieferanten";
import "../../styles/lieferanten.css";

export type GewaehltesMaterial = { id: string; name: string; einheit: string; art?: MaterialArt };

/** Namen, die nach Gerät klingen — beim Anlegen ist dann „Maschine" vorgewählt. */
const MASCHINE = /(häcksler|bagger|radlader|lader|rüttel|walze|fräse|dumper|kran|hebebühne|arbeitsbühne|bühne|kettensäge|motorsäge|stubbenfräse|kompressor|aggregat|maschine|gerät)/i;


type Props = {
  onWahl: (m: GewaehltesMaterial) => void;
  onAbbruch?: () => void;
  /** Schon vorhandene Materialien — erscheinen als „schon drin". */
  vorhanden?: string[];
  platzhalter?: string;
  /** Vorbelegter Suchtext, z. B. der Titel einer Angebotsposition. */
  start?: string;
  /** Treffer erst beim Tippen zeigen — für ein Suchfeld, das immer offen steht. */
  nurBeiEingabe?: boolean;
  autoFokus?: boolean;
};

export default function MaterialWahl({ onWahl, onAbbruch, vorhanden = [], platzhalter = "Material suchen …", start = "", nurBeiEingabe = false, autoFokus = true }: Props) {
  const [q, setQ] = useState(start);
  const [such, setSuch] = useState(start);
  const [neu, setNeu] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setSuch(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);
  const zeigen = !nurBeiEingabe || q.trim().length > 0;
  const treffer = useMaterialSuche(such, !neu && zeigen);
  const liste: MaterialStamm[] = treffer.data?.materialien ?? [];
  const genau = liste.some((m) => m.name.trim().toLowerCase() === q.trim().toLowerCase());

  if (neu) {
    return <MaterialNeu name={q.trim()} onFertig={(m) => { setNeu(false); onWahl(m); }} onZurueck={() => setNeu(false)} />;
  }

  return (
    <div className="lf-mwahl">
      <div className="lf-mwahl-zeile">
        <input
          className="lf-feld"
          autoFocus={autoFokus}
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onAbbruch?.(); } }}
          placeholder={platzhalter}
          aria-label="Material suchen"
        />
        {onAbbruch && <button type="button" className="lf-knopf" onClick={onAbbruch}>Abbrechen</button>}
      </div>
      {zeigen && <div className="lf-mwahl-liste" role="listbox" aria-label="Materialien">
        {treffer.isPending && <div className="lf-leer">Suche …</div>}
        {liste.map((m) => {
          const drin = vorhanden.includes(m.id);
          return (
            <button key={m.id} type="button" role="option" aria-selected={false} className="lf-mwahl-option" disabled={drin}
              onClick={() => onWahl({ id: m.id, name: m.name, einheit: m.einheit, art: m.art })}>
              <span className="lf-mwahl-name">{m.name}{m.art === "maschine" && <em className="lf-art">Maschine</em>}</span>
              <span className="lf-mwahl-info">
                {[m.einheit, m.standard_name ? `Standard: ${m.standard_name}` : "", drin ? "schon in der Liste" : ""].filter(Boolean).join(" · ")}
              </span>
            </button>
          );
        })}
        {!treffer.isPending && liste.length === 0 && q.trim() && <div className="lf-leer">Nichts gefunden.</div>}
        {q.trim() && !genau && (
          <button type="button" className="lf-mwahl-option is-neu" onClick={() => setNeu(true)}>
            <span className="lf-mwahl-name">＋ „{q.trim()}“ neu in den Katalog aufnehmen</span>
          </button>
        )}
      </div>}
    </div>
  );
}

function MaterialNeu({ name: start, onFertig, onZurueck }: { name: string; onFertig: (m: GewaehltesMaterial) => void; onZurueck: () => void }) {
  const listen = useMaterialStammlisten();
  const anlegen = useMaterialAnlegen();
  const [name, setName] = useState(start);
  const [art, setArt] = useState<MaterialArt>(MASCHINE.test(start) ? "maschine" : "material");
  const [einheit, setEinheit] = useState("");
  const [kategorie, setKategorie] = useState("");
  return (
    <div className="lf-form">
      {/* Material oder Maschine — die übergeordnete Art (Benjamin, 25.09.2026). */}
      <div className="lf-art-wahl" role="group" aria-label="Art">
        {(listen.data?.arten ?? [{ slug: "material" as MaterialArt, label: "Material" }, { slug: "maschine" as MaterialArt, label: "Maschine" }]).map((a) => (
          <button key={a.slug} type="button" className={"lf-knopf" + (art === a.slug ? " ist-an" : "")} aria-pressed={art === a.slug}
            onClick={() => { setArt(a.slug); if (a.slug === "maschine") setKategorie(""); }}>
            {a.label}
          </button>
        ))}
      </div>
      <label className="lf-label">{art === "maschine" ? "Neue Maschine" : "Neues Material"}
        <input className="lf-feld" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <div className="lf-form-zwei">
        <label className="lf-label">Einheit
          <select className="lf-feld" value={einheit} onChange={(e) => setEinheit(e.target.value)}>
            <option value="">—</option>
            {(listen.data?.einheiten ?? []).map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </label>
        <label className="lf-label">Kategorie
          <select className="lf-feld" value={kategorie} onChange={(e) => setKategorie(e.target.value)} disabled={art === "maschine"}>
            <option value="">—</option>
            {(listen.data?.kategorien ?? []).map((k) => <option key={k.slug} value={k.slug}>{k.label}</option>)}
          </select>
        </label>
      </div>
      {anlegen.isError && <div className="lf-fehler">{fehlerText(anlegen.error)}</div>}
      <div className="lf-knoepfe">
        <button type="button" className="lf-knopf" onClick={onZurueck}>Zurück</button>
        <button type="button" className="lf-knopf is-primaer" disabled={!name.trim() || anlegen.isPending}
          onClick={() => anlegen.mutate({ name: name.trim(), einheit, kategorie, art },
            { onSuccess: (m) => onFertig({ id: m.id, name: m.name, einheit: m.einheit, art: m.art }) })}>
          {anlegen.isPending ? "Legt an …" : "Anlegen"}
        </button>
      </div>
    </div>
  );
}
